import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { useT } from "../../../core/i18n";
import Api from "../../../core/data/redux/utils/apiCall";
import { RemarksCards } from "../otherServices/leaveEncashment/LeaveEncashmentDetail";
import {
  approveRejectLeaveEncashment,
  buildRemarksRows,
  displayEmpId,
  fetchApproverDetails,
  findOneLeaveEncashment,
  formatIsoToDmy,
  getRequestStatusUi,
} from "../otherServices/leaveEncashment/leaveEncashment.service";

const NO_DATA = "-";

const LeaveEncashmentApproval: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const { requestID } = useParams<{ requestID: string }>();
  const refId = requestID ? decodeURIComponent(requestID) : "";

  const [detail, setDetail] = useState<any>(null);
  const [approvers, setApprovers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [remarks, setRemarks] = useState("");
  const [remarkError, setRemarkError] = useState("");
  const [confirmReject, setConfirmReject] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const label = (key: string, fallback: string) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  useEffect(() => {
    const load = async () => {
      if (!refId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await findOneLeaveEncashment(refId);
        if (!data) {
          setDetail(null);
          toast.error(label("failed_to_load_details", "Failed to load details"));
          return;
        }
        setDetail(data);
        setApprovers(await fetchApproverDetails(displayEmpId(data), refId));
      } catch (err: any) {
        setDetail(null);
        toast.error(Api.getErrorMessage(err) || label("failed_to_load_details", "Failed to load details"));
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  const submit = async (action: "A" | "R") => {
    setSubmitting(true);
    try {
      const result = await approveRejectLeaveEncashment({
        ReferenceNumber: refId,
        ApproverAction: action,
        Comment: remarks,
      });
      if (!result.ok) {
        toast.error(result.message || label("something_went_wrong", "Something went wrong"));
        return;
      }
      toast.success(
        result.message ||
          (action === "A"
            ? t("Application has been approved")
            : t("Application has been rejected"))
      );
      navigate(routes.todoWorklist.tasks);
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setSubmitting(false);
      setConfirmReject(false);
    }
  };

  const statusUi = getRequestStatusUi(detail?.RequestStatus || "", t);
  const rows = buildRemarksRows(approvers, t);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div>
            <h3 className="page-title mb-1">{label("leave_encashment_details", "Leave Encashment Details")}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item"><Link to={routes.todoWorklist.tasks}>To-Do Worklist</Link></li>
                <li className="breadcrumb-item active" aria-current="page">{label("leave_encashment_details", "Leave Encashment Details")}</li>
              </ol>
            </nav>
          </div>
          <button type="button" className="btn btn-light" onClick={() => navigate(-1)} disabled={submitting}>
            <i className="ti ti-arrow-left me-1"></i>
            {t("back")}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>
        ) : !detail ? (
          <div className="card border-0 shadow-sm"><div className="card-body text-center py-5 text-muted">{label("no_details_available", "No details available")}</div></div>
        ) : (
          <>
            <div className="alert alert-light d-flex justify-content-between align-items-center">
              <span className="fw-semibold">{label("request_status", "Request Status")}</span>
              <span className={statusUi.badge}>{statusUi.label}</span>
            </div>
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white fw-bold">{label("request_information", "Request Information")}</div>
              <div className="card-body">
                <InfoRow label={label("reference_no", "Reference No")} value={detail.ReferenceNumber || refId} />
                <InfoRow label={label("employee_id", "Employee ID")} value={displayEmpId(detail) || undefined} />
                <InfoRow label={t("created_at")} value={detail.created_at ? formatIsoToDmy(detail.created_at) : NO_DATA} />
              </div>
            </div>
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white fw-bold">{t("leave_details")}</div>
              <div className="card-body">
                <InfoRow label={label("leave_type", "Leave Type")} value={detail.QuotaText} />
                <InfoRow label={label("leave_encashment_method", "Leave Encashment Method")} value={detail.Description} />
                <InfoRow label={t("days_to_be_encashed")} value={detail.QuotaNumber} />
                <InfoRow label={label("quota_type", "Quota Type")} value={detail.QuotaText} />
                {!!detail.OffCycleIndicator && <InfoRow label={t("off_cycle_indicator")} value={detail.OffCycleIndicator} />}
              </div>
            </div>
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white fw-bold">{t("remarks")}</div>
              <div className="card-body">
                <RemarksCards
                  referenceNumber={detail.ReferenceNumber || refId}
                  rows={rows}
                  emptyText={label("no_data_available", "No data available")}
                  referenceLabel={label("reference_no", "Reference No")}
                  nameLabel={label("name", "Name")}
                  remarkLabel={t("remarks")}
                />
              </div>
            </div>
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white fw-bold">{label("add_remarks", "Add Remarks")}</div>
              <div className="card-body">
                <label className="form-label fw-semibold">{t("remarks")}</label>
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder={t("write_something")}
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(e.target.value);
                    if (e.target.value.trim()) setRemarkError("");
                  }}
                />
                {remarkError && <div className="text-danger small mt-1">{remarkError}</div>}
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-danger"
                disabled={submitting}
                onClick={() => {
                  if (!remarks.trim()) {
                    setRemarkError(label("remarks_required", "Remarks are required"));
                    return;
                  }
                  setConfirmReject(true);
                }}
              >
                {label("reject", "Reject")}
              </button>
              <button type="button" className="btn btn-success" disabled={submitting} onClick={() => submit("A")}>{label("approve", "Approve")}</button>
            </div>
          </>
        )}
      </div>

      {confirmReject && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.45)" }} role="dialog">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">{label("reject", "Reject")}</h5></div>
              <div className="modal-body">{label("are_you_sure_want_to_reject", "Are you sure you want to reject?")}</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" disabled={submitting} onClick={() => setConfirmReject(false)}>{t("cancel")}</button>
                <button type="button" className="btn btn-danger" disabled={submitting} onClick={() => submit("R")}>{label("reject", "Reject")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value ?? NO_DATA}</span>
  </div>
);

export default LeaveEncashmentApproval;
